import { isAxiosError } from "axios";
import { useCallback, useState } from "react";
import type {
    AgentSkill,
    AgentSkillFlaggedFinding,
    AgentSkillWriteResponse,
} from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useArchiveAgentSkillMutation,
    useDisableAgentSkillMutation,
    useEnableAgentSkillMutation,
    useResetAgentSkillMutation,
    useRestoreAgentSkillRevisionMutation,
    useUpdateAgentSkillMutation,
} from "@api/agentSkill.api";
import { consistencyErrorToFinding } from "./agentSkillChat";
import {
    RESET_CONFIRMATION_QUESTION,
    RESET_TO_DEFAULT_LABEL,
    SCREEN_UNAVAILABLE_MESSAGE,
} from "./agentSkillCopy";
import {
    AgentSkillFormValues,
    buildUpdateAgentSkillRequest,
    getEditAgentSkillForm,
} from "./agentSkillForm";

interface LifecycleSkill {
    skillId: string;
    title: string;
}

// The server's message is user-facing copy; anything else gets the fallback.
export const getAgentSkillErrorMessage = (error: Error, fallback: string) => {
    if (!isAxiosError(error)) {
        return fallback;
    }
    if (error.response?.status === 503) {
        return SCREEN_UNAVAILABLE_MESSAGE;
    }
    return typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : fallback;
};

const isString = (value: unknown): value is string => typeof value === "string";

const isFinding = (value: unknown): value is AgentSkillFlaggedFinding =>
    typeof value === "object" &&
    value !== null &&
    "code" in value &&
    typeof value.code === "string" &&
    "excerpt" in value &&
    typeof value.excerpt === "string";

// A 409 from the injection screen carries the flagged passages.
export const getAgentSkillErrorFindings = (
    error: Error,
): AgentSkillFlaggedFinding[] => {
    if (!isAxiosError(error) || error.response?.status !== 409) {
        return [];
    }
    const errors: unknown = error.response.data?.errors;
    const findings: unknown = error.response.data?.findings;
    return [
        ...(Array.isArray(errors)
            ? errors.filter(isString).map(consistencyErrorToFinding)
            : []),
        ...(Array.isArray(findings) ? findings.filter(isFinding) : []),
    ];
};

// Saves the editor's changes. A save with nothing changed closes without a
// request, because the API refuses an empty update. Resolves to the saved
// skill, which is a different row when the first edit of a Kick default forks
// the firm's override. The body is compared against the editor's own
// serialization of the persisted text, so an untouched document is no change.
export const useAgentSkillUpdate = () => {
    const { toast } = useToaster();
    const { mutateAsync: updateSkill } = useUpdateAgentSkillMutation();
    return useCallback(
        async ({
            skill,
            values,
            normalizedBody,
        }: {
            skill: AgentSkill;
            values: AgentSkillFormValues;
            normalizedBody: string;
        }): Promise<AgentSkillWriteResponse | null> => {
            const body = buildUpdateAgentSkillRequest({
                values,
                initialValues: {
                    ...getEditAgentSkillForm(skill),
                    body: normalizedBody,
                },
                kind: skill.kind,
            });
            if (Object.keys(body).length === 0) {
                return null;
            }
            const updated = await updateSkill({ skillId: skill.id, body });
            toast(`Updated skill "${updated.title}"`);
            return updated;
        },
        [updateSkill, toast],
    );
};

export const useAgentSkillLifecycle = ({
    onDeleted,
    onReset,
}: {
    onDeleted?: () => void;
    onReset?: (global: AgentSkill) => void;
} = {}) => {
    const { toast, error: errorToast } = useToaster();
    const [deletingSkill, setDeletingSkill] = useState<LifecycleSkill | null>(
        null,
    );
    const [resettingSkill, setResettingSkill] = useState<LifecycleSkill | null>(
        null,
    );
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const { mutateAsync: enableSkill } = useEnableAgentSkillMutation();
    const { mutateAsync: disableSkill } = useDisableAgentSkillMutation();
    const { mutateAsync: archiveSkill } = useArchiveAgentSkillMutation();
    const { mutateAsync: resetSkill } = useResetAgentSkillMutation();
    const { mutateAsync: restoreRevision } =
        useRestoreAgentSkillRevisionMutation();

    const run = useCallback(
        async <T,>({
            action,
            successMessage,
            failureMessage,
        }: {
            action: () => Promise<T>;
            successMessage: string;
            failureMessage: string;
        }): Promise<T | null> => {
            try {
                const result = await action();
                toast(successMessage);
                return result;
            } catch (error) {
                errorToast(
                    error instanceof Error
                        ? getAgentSkillErrorMessage(error, failureMessage)
                        : failureMessage,
                );
                return null;
            }
        },
        [toast, errorToast],
    );

    const enable = useCallback(
        async ({ skillId, title }: LifecycleSkill) =>
            (await run({
                action: () => enableSkill(skillId),
                successMessage: `Enabled skill "${title}"`,
                failureMessage: `Failed to enable skill "${title}"`,
            })) !== null,
        [run, enableSkill],
    );

    const disable = useCallback(
        async ({ skillId, title }: LifecycleSkill) =>
            (await run({
                action: () => disableSkill(skillId),
                successMessage: `Disabled skill "${title}"`,
                failureMessage: `Failed to disable skill "${title}"`,
            })) !== null,
        [run, disableSkill],
    );

    const restore = useCallback(
        async ({
            skillId,
            revisionId,
            version,
        }: {
            skillId: string;
            revisionId: string;
            version: number;
        }) =>
            await run({
                action: () => restoreRevision({ skillId, revisionId }),
                successMessage: `Restored version ${version}`,
                failureMessage: `Failed to restore version ${version}`,
            }),
        [run, restoreRevision],
    );

    const requestDelete = useCallback((skill: LifecycleSkill) => {
        setDeletingSkill(skill);
        setIsDeleteModalOpen(true);
    }, []);

    const requestReset = useCallback((skill: LifecycleSkill) => {
        setResettingSkill(skill);
        setIsResetModalOpen(true);
    }, []);

    const deleteModal = deletingSkill && (
        <ConfirmationModal
            show={isDeleteModalOpen}
            title={`Delete "${deletingSkill.title}"?`}
            question="Agent runs will stop using this skill."
            yes="Delete"
            yesBtnVariant="destructive"
            no="Cancel"
            onReject={() => setIsDeleteModalOpen(false)}
            onHide={() => setIsDeleteModalOpen(false)}
            onConfirm={async () => {
                const { skillId, title } = deletingSkill;
                const result = await run({
                    action: () => archiveSkill(skillId),
                    successMessage: `Deleted skill "${title}"`,
                    failureMessage: `Failed to delete skill "${title}"`,
                });
                if (result !== null) {
                    setIsDeleteModalOpen(false);
                    onDeleted?.();
                }
            }}
        />
    );

    const resetModal = resettingSkill && (
        <ConfirmationModal
            show={isResetModalOpen}
            title={`Reset "${resettingSkill.title}" to Kick's default?`}
            question={RESET_CONFIRMATION_QUESTION}
            yes={RESET_TO_DEFAULT_LABEL}
            no="Cancel"
            onReject={() => setIsResetModalOpen(false)}
            onHide={() => setIsResetModalOpen(false)}
            onConfirm={async () => {
                const { skillId, title } = resettingSkill;
                const global = await run({
                    action: () => resetSkill(skillId),
                    successMessage: `Reset skill "${title}" to default`,
                    failureMessage: `Failed to reset skill "${title}"`,
                });
                if (global !== null) {
                    setIsResetModalOpen(false);
                    onReset?.(global);
                }
            }}
        />
    );

    return {
        enable,
        disable,
        restore,
        requestDelete,
        requestReset,
        deleteModal,
        resetModal,
    };
};
